// src/components/NotesSection.jsx
import { useState, useEffect, useCallback } from 'react';
import Pagination from 'rc-pagination';
import PaginationLocale from 'rc-pagination/lib/locale/ko_KR';
import { Note } from 'types/notes';
import { useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import NotesIndex2NoteItem from './NotesIndex2NoteItem';

interface ApiResponse {
  items: Note[];
  page: number;
  total_count: number;
  per_page: number;
}

interface PboardApiResponse {
  id: number
  pbid: string;
  name: string;
  short_name: string;
  category: string;
}

interface NotesIndex2Props {
  currentNoteId: number;
  page: number;
}

// category_str to array of [key, value] pair
const parseCategorStr = (category_str: string): Array<[string, string]> => {
  let categories = category_str.split(',');
  categories = categories.filter((item) => item !== '');
  const categoriesArray: Array<[string, string]> = categories.map((category) => [category, category]);
  categoriesArray.unshift(['전체', '']);
  return categoriesArray;
};


const NotesIndex2 = ({ currentNoteId, page }: NotesIndex2Props) => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageCount, setPageCount] = useState<number>(0);
  const itemsPerPage: number = 6; // Adjust based on how many notes you want per page
  const [totalCount, setTotalCount] = useState(0);
  const [currentNotes, setCurrentNotes] = useState<Note[]>([]); //useState(dummyNotes.slice(indexOfFirstNote, indexOfLastNote));
  const [searchParams, setSearchParams] = useSearchParams();
  const [categories, setCategories] = useState<Array<[string, string]>>(new Array());

  const fetchPboardInfo = useCallback(async () => {
    setLoading(true);

    const pbid = 'notes';
    try {
      const { data } = await axios.get<PboardApiResponse>('/api/v1/pboard?pbid=' + pbid);
      const categoryArr = parseCategorStr(data.category);
      setCategories(categoryArr);
      //console.log('categoryArr', categoryArr);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchNotes = useCallback(async (page: number, per_page: number, category: string) => {
    setLoading(true);

    try {
      // get items
      const params = new URLSearchParams({ page: page.toString(), per_page: per_page.toString(), category: category });
      const { data } = await axios.get<ApiResponse>(`/api/v1/notes?${params.toString()}`);
      setCurrentNotes(data.items);
      setTotalCount(data.total_count);
      setPageCount(Math.ceil(data.total_count / data.per_page));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [categories, activeTab]);

  useEffect(() => {
    fetchPboardInfo();
  }, [fetchPboardInfo]);

  useEffect(() => {
    const page = searchParams.get('page');
    page ? setCurrentPage(parseInt(page)) : setCurrentPage(1);
  }, [searchParams]);

  useEffect(() => {
    //console.log('🔥 currentPage changed, calling fetchNotes', currentPage);
    window.scrollTo(0, 0);
    fetchNotes(currentPage, itemsPerPage, activeTab);
  }, [currentPage, activeTab, fetchNotes]);

  const onPageChange = (page: number) => {
    console.log('onPageChange event', page);
    if (page < 1) page = 1;
    else if (page > pageCount) page = pageCount;
    setCurrentPage(page);
    setSearchParams({ page: page.toString() });
  };

  // const onChangeCategory = (category: string) => {
  //   console.log('onChangeCategory event', category);
  //   setActiveTab(category);
  //   setCurrentPage(1);
  //   setSearchParams({ page: '1', category: category });
  // }

  if (loading) {
    return <>
      <div className='flex justify-center px-4 py-2'>
        <span className="loading loading-spinner sm:loading-sm lg:loading-lg"></span>
      </div>
    </>;
  }

  {
    !loading && currentNotes.length === 0 &&
      <div className='flex justify-center px-4 py-30'>노트가 없습니다.</div>
  }

  return (
    <div>
      <div className="max-w mx-auto w-full border-t-2 border-b border-black">
        <ul className="divide-y divide-black">
          {currentNotes.map(note => ( // Use currentNotes for rendering
            <NotesIndex2NoteItem key={note.id} note={note} currentNoteId={currentNoteId} page={page} pboard={searchParams.get('pboard') || 'notes'} />
          ))}
        </ul>
      </div>

      {/* <div className="flex justify-center mt-6 space-x-2">
        <button className="w-8 h-8 border border-black text-sm hover:bg-gray-100">1</button>
        <button className="w-8 h-8 border border-black text-sm hover:bg-gray-100">2</button>
      </div> */}

      <Pagination
        className="flex justify-center mt-6 space-x-2"
        total={totalCount}
        pageSize={itemsPerPage}
        current={currentPage}
        locale={PaginationLocale}
        showTitle={false}
        onChange={onPageChange}
        itemRender={(page, type, originalElement) => {
          if (type === 'page') {
            return (<button
              key={page}
              className={`w-8 h-8 border border-black text-sm hover:bg-gray-100 ${page === currentPage ? 'btn-active' : ''}`}
              onClick={() => onPageChange(page)}
            >
              {page}
            </button>);
          } else if (type === 'prev') {
            return (<button className="w-8 h-8 border border-black text-sm hover:bg-gray-100" disabled={currentPage === 1}
              onClick={() => onPageChange(currentPage - 1)}>«</button>);
          } else if (type === 'next') {
            return (<button className="w-8 h-8 border border-black text-sm hover:bg-gray-100" disabled={currentPage === pageCount}
              onClick={() => onPageChange(currentPage + 1)}>»</button>);
          }

          return originalElement;
        }}
      />
    </div>
  );
};

export default NotesIndex2;
